import {
  MutableRefObject,
  useCallback,
  useEffect,
  useRef,
  useState
} from "react";
import { DataSet } from "vis-data";
import { Edge, IdType, Network, NodeOptions, Options } from "vis-network";
import { connectLeafsToParent, generateNodesFromData } from "../graph-util";

import { CreateControlNodeOnlyFn, FileNodeExtra, RlNode } from "../@types";
import { useGraphEventContext } from "../context/RelationshipsGraphEventProvider";

const defaultOptions = {
  physics: {
    stabilization: {
      iterations: 100
    },
    minVelocity: 0.3,
    barnesHut: {
      gravitationalConstant: -80000,
      centralGravity: 0.3,
      springConstant: 0.07,
      damping: 0.45
    }
  },
  autoResize: true,
  nodes: {
    brokenImage: "/static/file_types-source-classification/default-unknown.png",
    shapeProperties: {
      interpolation: false // 'true' for intensive zooming
    }
  },
  edges: {
    smooth: false,
    color: "darkgray",
    width: 0.5,
    arrows: {
      to: {
        enabled: true,
        scaleFactor: 0.5
      }
    }
  }
} as Options;

type UnpinOptions = {
  unpinNode?: boolean;
  unpinChildren?: boolean;
};

type UnpinOptionsKey = keyof UnpinOptions;

interface Props {
  initNodes: Array<RlNode<FileNodeExtra>>;
  initEdges: Array<Edge>;
  canvasRef: MutableRefObject<HTMLDivElement | null>;
  networkOptions?: Options;
}
const useVisNetwork = ({
  initNodes,
  initEdges,
  canvasRef,
  networkOptions = defaultOptions
}: Props) => {
  const nodesRef = useRef(new DataSet(initNodes));
  const edgesRef = useRef(new DataSet(initEdges));
  const networkRef = useRef<Network>();

  const { notifyUpdateSidebarDataEvent } = useGraphEventContext();
  const [selectedNode, setSelectedNode] = useState<RlNode | null>(
    nodesRef.current.get("rootNode") as RlNode
  );

  const rerenderControlNode = (controlNode: RlNode): RlNode | null => {
    const network = networkRef.current;
    const nodes = nodesRef.current;
    if (!network || !controlNode.id) return null;
    const pos = network.getPositions([controlNode.id])[controlNode.id];
    nodes.remove(controlNode.id);
    controlNode = {
      ...controlNode,
      x: pos.x,
      y: pos.y,
      fixed: true
    };
    nodes.update(controlNode);
    return controlNode;
  };

  const addData = useCallback(
    (data: { nodes: RlNode[]; edges: Edge[] }) => {
      const nodes = nodesRef.current;
      const edges = edgesRef.current;
      nodes.update(data.nodes);
      edges.update(data.edges);
      notifyUpdateSidebarDataEvent(data.nodes);
    },
    [notifyUpdateSidebarDataEvent]
  );

  const getNodeChildren = useCallback((parentNodeId: string) => {
    const network = networkRef.current;
    const nodes = nodesRef.current;
    const childrenIds = network?.getConnectedNodes(parentNodeId, "to") || [];
    return nodes.get(childrenIds as string[]);
  }, []);

  const getNodeChildrenVisible = useCallback(
    (parentNodeId: string) => {
      if (!parentNodeId) return [];
      const children = getNodeChildren(parentNodeId);
      const allNodesHidden = children.every(child => child.hidden);
      // special case: if control node is clicked, all children can be hidden. In that case return all children
      return allNodesHidden
        ? children
        : children.filter(child => !child.hidden);
    },
    [getNodeChildren]
  );

  const renderControlNode = useCallback<CreateControlNodeOnlyFn>(
    ({ generateControlNode }) => {
      const network = networkRef.current;
      const nodes = nodesRef.current;
      const edges = edgesRef.current;

      if (!network || !nodes || !edges) return;

      const controlNodeObj = generateControlNode(
        network
      ) as RlNode<FileNodeExtra>;

      if (!controlNodeObj.id) return;

      const controlNodeExists = Boolean(nodes.get(controlNodeObj.id));
      if (!controlNodeExists) {
        addData(connectLeafsToParent([controlNodeObj], "rootNode"));
        network.fit({ maxZoomLevel: 0.35 });
      }
    },
    [addData]
  );

  const renderControlNodeWithChildren = useCallback(
    ({
      data,
      generateControlNode,
      itemToNodeMapFn
    }: {
      data: Array<unknown>;
      generateControlNode: any;
      itemToNodeMapFn: any;
    }) => {
      const network = networkRef.current;
      const nodes = nodesRef.current;
      const edges = edgesRef.current;

      if (!data?.length || !itemToNodeMapFn || !network || !nodes || !edges)
        return;

      const controlNodeObj = generateControlNode(network);
      const controlNodeExists = Boolean(nodes.get(controlNodeObj.id));
      if (!controlNodeExists) {
        addData(connectLeafsToParent([controlNodeObj], "rootNode"));
      }
      const controlNode =
        controlNodeExists && rerenderControlNode(controlNodeObj);
      const childrenNodes = generateNodesFromData(
        data,
        itemToNodeMapFn,
        controlNode ? { x: controlNode.x, y: controlNode.y } : {}
      );
      addData(connectLeafsToParent(childrenNodes, controlNodeObj.id));
    },
    [addData]
  );

  const toggleChildrenVisibility = useCallback(
    (nodeId: string) => {
      if (nodeId === "rootNode" || selectedNode?.id !== nodeId) {
        return;
      }
      const network = networkRef.current;
      const nodes = nodesRef.current;
      const connectedIds =
        (network?.getConnectedNodes(nodeId, "to") as string[]) ?? [];
      // click show/hide
      if (network && connectedIds.length) {
        connectedIds.forEach(connectedId => {
          if (nodeId === "rootNode") return;
          const child = nodes.get(connectedId);
          if (!child?.id) return;
          const pos = network.getPositions(child.id)[child.id];
          nodes.update({
            ...child,
            hidden: !child.hidden,
            x: pos.x,
            y: pos.y
          });
        });
      }
    },
    [selectedNode]
  );

  const handleClickCb = useCallback(
    (event: { nodes: string[] }) => {
      const [clickedNodeId] = event.nodes;
      if (!clickedNodeId) {
        setSelectedNode(null);
        return;
      }

      toggleChildrenVisibility(clickedNodeId);
    },
    [toggleChildrenVisibility]
  );
  const showTopX = useCallback(
    (ctrlNode: string, x: number) => {
      const network = networkRef.current;
      const nodes = nodesRef.current;
      if (!network) return;
      const children =
        (network?.getConnectedNodes(ctrlNode, "to") as string[]) ?? [];
      const pos = network.getPositions([ctrlNode])[ctrlNode];
      const updatedNodes: RlNode[] = [];
      children.forEach((element, index) => {
        const child = nodes.get(element);
        const updatedChild = {
          ...child,
          x: pos.x,
          y: pos.y,
          hidden: x ? !(index < x) : false
        } as RlNode;
        nodes.update(updatedChild);
        if (updatedChild.hidden) {
          updatedNodes.push(updatedChild);
        }
      });
      notifyUpdateSidebarDataEvent(updatedNodes);
    },
    [notifyUpdateSidebarDataEvent]
  );

  const unpinNodeWithChildren = useCallback(
    (
      nodeId: string,
      unpinOptions: UnpinOptions = {
        unpinNode: true,
        unpinChildren: false
      }
    ) => {
      const network = networkRef.current;
      const nodes = nodesRef.current;
      if (!network) return;
      const pos = network.getPositions([nodeId])[nodeId];
      const functionsMap = {
        unpinNode: () =>
          nodes.update({
            ...nodes.get(nodeId),
            fixed: false,
            x: pos.x,
            y: pos.y
          }),
        unpinChildren: () => {
          const children =
            (network?.getConnectedNodes(nodeId, "to") as string[]) ?? [];
          children?.forEach(childId =>
            nodes.update({
              ...nodes.get(childId),
              fixed: false,
              x: pos.x,
              y: pos.y
            })
          );
        }
      };
      Object.entries(functionsMap).forEach(([optionName, func]) => {
        if (unpinOptions[optionName as UnpinOptionsKey]) {
          func();
        }
      });
    },
    []
  );

  useEffect(() => {
    if (!canvasRef.current) return;
    networkRef.current = new Network(
      canvasRef.current,
      {
        nodes: nodesRef.current,
        edges: edgesRef.current
      },
      networkOptions
    );
    const network = networkRef.current;
    const nodes = nodesRef.current;

    const updateNode = (id: IdType, nodeProps: NodeOptions) => {
      nodes.update({ id, ...nodeProps });
      return nodes.get(id);
    };

    network.once("afterDrawing", () => {
      if (!canvasRef.current) return;
      network.fit({
        maxZoomLevel: 0.3,
        animation: {
          duration: 1000,
          easingFunction: "easeOutQuad"
        }
      });
    });

    network.on("dragStart", event => {
      const [clickedNode] = event.nodes;
      if (!clickedNode) return;
      setSelectedNode(updateNode(clickedNode, { fixed: false }));
    });

    network.on("dragEnd", event => {
      const [clickedNode] = event.nodes;
      if (!clickedNode) return;
      setSelectedNode(updateNode(clickedNode, { fixed: true }));
    });

    network.on("selectNode", event => {
      const [selectNode] = event.nodes;
      if (!selectNode) return;
      setSelectedNode(nodes.get(selectNode) as unknown as RlNode);
      network?.focus(selectNode, {
        animation: {
          duration: 300,
          easingFunction: "easeInQuad"
        }
      });
    });
  }, []); // render only initially!!!!

  useEffect(() => {
    const network = networkRef.current;
    network?.on("click", handleClickCb);
    return () => {
      network?.off("click", handleClickCb);
    };
  }, [handleClickCb]);

  return {
    network: networkRef.current,
    nodes: nodesRef.current,
    edges: edgesRef.current,
    selectedNode,
    graphActions: {
      selectedNode,
      showTopX,
      unpinNodeWithChildren,
      renderControlNodeWithChildren,
      renderControlNode,
      toggleChildrenVisibility,
      getNodeChildren,
      getNodeChildrenVisible
    }
  };
};

export default useVisNetwork;
